import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import { useGetProductsQuery } from "state/api";
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
  formatCurrency,
} from "components/shared";

// There is no subscription / plan backend for bharatstore.org — "pricing" in
// this e-commerce admin means the cost/selling price of the catalogue. This
// page surfaces the real per-product pricing (purchase price, selling price,
// tax and profit margin) straight from the products API.

// Colour the margin from theme semantic keys so weak margins read at a glance.
const marginColor = (value) => {
  const m = Number(value);
  if (!Number.isFinite(m) || m <= 0) return "error.main";
  if (m < 15) return "warning.main";
  return "success.main";
};

const SummaryCard = ({ label, value }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h5" color="text.primary" sx={{ fontWeight: 700, mt: 0.5 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const PricingTable = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data, isFetching } = useGetProductsQuery({
    search,
    page: page + 1,
    limit: rowsPerPage,
  });

  const { rows, total } = useMemo(
    () => normalizeList(data, { page: page + 1, pageSize: rowsPerPage }),
    [data, page, rowsPerPage]
  );

  const summary = useMemo(() => {
    if (!rows.length) {
      return { avgPrice: 0, avgMargin: 0, thinMargins: 0 };
    }
    const totalPrice = rows.reduce((sum, p) => sum + (Number(p.sellingPrice) || 0), 0);
    const totalMargin = rows.reduce((sum, p) => sum + (Number(p.profitMargin) || 0), 0);
    const thinMargins = rows.filter((p) => Number(p.profitMargin) < 15).length;
    return {
      avgPrice: totalPrice / rows.length,
      avgMargin: totalMargin / rows.length,
      thinMargins,
    };
  }, [rows]);

  const columns = useMemo(
    () => [
      {
        field: "productCode",
        headerName: "Code",
        render: (p) => p.productCode || p._id?.slice(-6).toUpperCase(),
      },
      {
        field: "name",
        headerName: "Product",
        render: (p) => p.name || "—",
      },
      {
        field: "category",
        headerName: "Category",
        render: (p) => p.category || "—",
      },
      {
        field: "purchasePrice",
        headerName: "Cost",
        align: "right",
        render: (p) => formatCurrency(p.purchasePrice),
      },
      {
        field: "sellingPrice",
        headerName: "Selling Price",
        align: "right",
        render: (p) => formatCurrency(p.sellingPrice),
      },
      {
        field: "tax",
        headerName: "Tax",
        align: "right",
        render: (p) => (p.tax != null ? `${p.tax}%` : "—"),
      },
      {
        field: "profitMargin",
        headerName: "Margin",
        align: "right",
        render: (p) =>
          p.profitMargin != null ? (
            <Typography
              variant="body2"
              component="span"
              sx={{ color: marginColor(p.profitMargin), fontWeight: 600 }}
            >
              {Number(p.profitMargin).toFixed(1)}%
            </Typography>
          ) : (
            "—"
          ),
      },
      {
        field: "status",
        headerName: "Status",
        render: (p) => <StatusChip status={p.status} />,
      },
      {
        field: "actions",
        headerName: "",
        align: "right",
        render: (p) => (
          <Tooltip title="Edit pricing">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/products/add-new?id=${p._id}`);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate]
  );

  const exportCsv = () => {
    const header = ["Code", "Product", "Category", "Cost", "Selling Price", "Tax %", "Margin %", "Status"];
    const lines = rows.map((p) =>
      [
        p.productCode || p._id?.slice(-6),
        `"${(p.name || "").replace(/"/g, '""')}"`,
        p.category || "",
        p.purchasePrice ?? "",
        p.sellingPrice ?? "",
        p.tax ?? "",
        p.profitMargin ?? "",
        p.status || "",
      ].join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "product-pricing.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer
      title="Pricing"
      subtitle="Cost, selling price, tax and profit margin for every product in the catalogue"
    >
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Products priced" value={total} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Avg. selling price" value={formatCurrency(summary.avgPrice)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Avg. margin" value={`${summary.avgMargin.toFixed(1)}%`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Thin margins (< 15%)" value={summary.thinMargins} />
        </Grid>
      </Grid>

      <TableToolbar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search products by name…"
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={exportCsv}
              disabled={rows.length === 0}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/products/add-new")}
            >
              Add product
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        emptyText="No products to price yet"
        onRowClick={(p) => navigate(`/products/add-new?id=${p._id}`)}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
      />
    </PageContainer>
  );
};

export default PricingTable;
